import React, { Component } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Link } from 'react-router-dom';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import loadable from '@loadable/component';

import { PRODUCT_REVIEWS_PER_PAGE } from 'constants/appConstants';
import { trackEvent } from 'helpers/analytics';
import { err, setError } from 'actions/errors';
import { redirectTo } from 'actions/redirect';
import {
  exitSearchReviews,
  fetchProductReviews,
  fetchReviewSearchResults,
  hideReviewGalleryModal,
  isValidReviewsRequest,
  loadProductReviewsPage,
  setProductReviewsDocMeta,
  showReviewGalleryModal,
  upvoteReview
} from 'actions/reviews';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import Pagination from 'components/common/Pagination';
import ReviewSearch from 'components/reviews/ReviewSearch';
import marketplace from 'cfg/marketplace.json';
import ReviewGalleryWrapper from 'containers/ReviewGalleryWrapper';
import ReviewSummary from 'components/productdetail/ReviewSummary';
import Review from 'components/reviews/Review';
import { makePageOneUrl } from 'helpers/ReviewUtils';
import MartyLink from 'components/common/MartyLink';
import ReviewSort from 'components/reviews/ReviewSort';
import { currentPathAndSearch } from 'helpers/ClientUtils';
import { titaniteView, track } from 'apis/amethyst';
import { evWriteProductReviewClick } from 'events/productReview';
import { ProductContextProvider } from 'components/productdetail/ProductContext';
import { PRODUCT_REVIEW_LIST_PAGE } from 'constants/amethystPageTypes';
import Breadcrumbs from 'components/common/Breadcrumbs';
import type { AppState } from 'types/app';
import { MartyContext } from 'utils/context';

import css from 'styles/containers/productReviews.scss';

const {
  features: { showReviews }
} = marketplace;
export const EmptyReviewLlama = loadable(() => import('components/animations/EmptyReviewLlama'));

const defaultProps = {
  trackEvent,
  track
};

interface Params {
  productId: string;
  reviewsPage?: string;
  reviewsStart?: string;
  orderBy?: string;
  reviewId?: string;
}
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & RouteComponentProps<Params, {}> & typeof defaultProps;

export class ProductReviews extends Component<Props> {
  static fetchDataOnServer(store: any, location: Location, params: Params) {
    const { productId, reviewsPage, reviewsStart, orderBy } = params;
    if (reviewsPage && +reviewsPage === 0) {
      const redirectAction = redirectTo(makePageOneUrl(location.pathname));
      return Promise.resolve(store.dispatch(redirectAction));
    } else if (isValidReviewsRequest(params)) {
      return store.dispatch(loadProductReviewsPage(productId, reviewsPage, reviewsStart, orderBy));
    } else {
      return Promise.resolve(store.dispatch(setError(err.PRODUCT_DETAILS, null, 404)));
    }
  }

  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);
    this.onNextPage = this.onNextPage.bind(this);
    this.onPreviousPage = this.onPreviousPage.bind(this);
    this.onSearchReviews = this.onSearchReviews.bind(this);
    this.onExitSearch = this.onExitSearch.bind(this);
  }

  componentDidMount() {
    const {
      product: { detail, reviewData },
      fetchProductReviews,
      params,
      setProductReviewsDocMeta
    } = this.props;

    const { productId, reviewsPage, reviewsStart, orderBy } = params;

    // only fetch data if loaded product is not requested productId
    const productChanged = !detail || detail.productId !== productId;
    const incomingPage = reviewsPage || 1;
    const incomingOffset = reviewsStart || 0;
    if (productChanged) {
      this.fetchAllData(params);
    } else {
      if (incomingPage !== reviewData.page || incomingOffset !== reviewData.offset) {
        fetchProductReviews(productId, reviewsPage, reviewsStart, true, orderBy);
      }
      detail && setProductReviewsDocMeta(detail);
    }

    titaniteView();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      reviewGallery: { isModalOpen },
      product: {
        isSearchingReviews,
        searchReviewData: { searchTerm }
      },
      hideReviewGalleryModal,
      params,
      fetchReviewSearchResults,
      fetchProductReviews
    } = this.props;
    const {
      params: { reviewsPage: prevReviewsPage, orderBy: prevOrderBy, productId: prevProductId }
    } = prevProps;
    const { productId, reviewsPage, reviewsStart, orderBy } = params;
    const productIdChanged = productId !== prevProductId;

    if (productIdChanged) {
      this.fetchAllData(params);
    } else if (reviewsPage !== prevReviewsPage || orderBy !== prevOrderBy) {
      if (isSearchingReviews) {
        if (!reviewsPage) {
          this.onExitSearch();
        } else {
          fetchReviewSearchResults(productId, searchTerm, reviewsPage);
        }
      } else {
        fetchProductReviews(productId, reviewsPage, reviewsStart, true, orderBy);
      }
    }
    if (isModalOpen && reviewsPage !== prevReviewsPage) {
      hideReviewGalleryModal();
    }
  }

  componentWillUnmount() {
    this.onExitSearch(false);
  }

  onExitSearch(backToReviews = true) {
    const {
      product: { detail, isSearchingReviews },
      exitSearchReviews
    } = this.props;
    const context = this.context as React.ContextType<typeof MartyContext>;
    if (isSearchingReviews && detail) {
      const { productId } = detail;
      const firstPage = this.makeReviewUrl(productId, 1, 0);
      exitSearchReviews();
      if (backToReviews) {
        context.router.pushPreserveAppRoot(firstPage);
      }
    }
  }

  onNextPage() {
    const { productId, reviewsPage, reviewsStart, reviewsTotalPages, orderBy } = this.getPaginationData();
    const context = this.context as React.ContextType<typeof MartyContext>;
    if (reviewsPage <= reviewsTotalPages) {
      const newLocation = this.makeReviewUrl(productId, reviewsPage + 1, reviewsStart, orderBy);
      context.router.pushPreserveAppRoot(newLocation);
    }
  }

  onPreviousPage() {
    const { productId, reviewsPage, reviewsStart, orderBy } = this.getPaginationData();
    const context = this.context as React.ContextType<typeof MartyContext>;
    if (reviewsPage > 0) {
      const newLocation = this.makeReviewUrl(productId, reviewsPage - 1, reviewsStart, orderBy);
      context.router.pushPreserveAppRoot(newLocation);
    }
  }

  onSearchReviews(e: React.FormEvent<HTMLFormElement>, searchTerm: string) {
    e.preventDefault();
    const {
      fetchReviewSearchResults,
      product: {
        detail,
        searchReviewData: { searchTerm: lastPerformedSearchTerm }
      }
    } = this.props;
    if (detail && searchTerm && searchTerm !== lastPerformedSearchTerm) {
      const { productId } = detail;
      fetchReviewSearchResults(productId, searchTerm);
    } else if (!searchTerm) {
      this.onExitSearch(true);
    }
  }

  getPaginationData() {
    const {
      product: {
        detail,
        isSearchingReviews,
        reviewData: { page, offset, orderBy },
        searchReviewData: { page: searchPage, offset: searchOffset, totalPages: searchTotalPages },
        reviewsTotalPages
      }
    } = this.props;

    const { productId } = detail || { productId: '' }; // detail can still be deliberately null.

    // this 100% should be in the reducer.
    // get appropriate offsets when user is searching reviews
    let reviewsPage, reviewsStart, totalPages;
    if (isSearchingReviews) {
      reviewsPage = searchPage;
      reviewsStart = searchOffset;
      totalPages = searchTotalPages;
    } else {
      reviewsPage = page;
      reviewsStart = offset;
      totalPages = reviewsTotalPages;
    }

    return {
      productId,
      reviewsPage: parseInt(reviewsPage as string, 10) || 1,
      reviewsStart: parseInt(reviewsStart as string, 10) || 0,
      reviewsTotalPages: parseInt(totalPages as string, 10) || 1,
      orderBy
    };
  }

  fetchAllData({
    productId,
    reviewsPage,
    reviewsStart,
    orderBy
  }: {
    productId: string;
    reviewsPage?: string;
    reviewsStart?: string;
    orderBy?: string;
  }) {
    this.props.loadProductReviewsPage(productId, reviewsPage, reviewsStart, orderBy);
  }

  handleReviewMediaClick = (reviewId: string, mediaIndex: number) => {
    const { showReviewGalleryModal } = this.props;
    showReviewGalleryModal(reviewId, mediaIndex);
  };

  handleReviewUpvoteClick = (reviewId: string) => {
    const {
      product: {
        reviewData: { loadingReviews }
      },
      upvoteReview
    } = this.props;
    if (!loadingReviews.includes(reviewId)) {
      upvoteReview(reviewId, currentPathAndSearch());
      trackEvent('TE_REVIEWS_RATEREVIEWHELPFUL');
    }
  };

  handleSortReviewsClick = (orderBy: string) => {
    const {
      params: { productId, reviewsStart, orderBy: orderByParam },
      exitSearchReviews
    } = this.props;
    const context = this.context as React.ContextType<typeof MartyContext>;
    if (orderByParam === orderBy) {
      return;
    }
    const newLocation = this.makeReviewUrl(productId, 1, reviewsStart, orderBy);
    exitSearchReviews();
    context.router.pushPreserveAppRoot(newLocation);
    trackEvent('TE_REVIEWS_SORTREVIEWBYNEW');
  };

  makePageHeading = () => {
    const {
      props: {
        product: { colorId, detail }
      }
    } = this;
    if (!detail) {
      return null;
    }
    const { brandName, productName } = detail;
    return (
      <MartyContext.Consumer>
        {({ testId }) => (
          <h1 className={css.pageHeading}>
            <span>Customer Reviews</span>{' '}
            <Link className={css.brandProductName} to={buildSeoProductUrl(detail, colorId)} data-test-id={testId('viewProduct')}>
              {brandName} {productName}
            </Link>
          </h1>
        )}
      </MartyContext.Consumer>
    );
  };

  handleWriteReviewClick = () => {
    const {
      track,
      params: { productId }
    } = this.props;
    track(() => [evWriteProductReviewClick, { productId, addedFrom: PRODUCT_REVIEW_LIST_PAGE }]);
  };

  makeReviewContent({
    reviewsTotalPages,
    reviewsStart,
    reviewsPage,
    orderBy
  }: {
    reviewsTotalPages: number;
    reviewsStart: number;
    reviewsPage: number;
    orderBy?: string;
  }) {
    const {
      product: { detail, reviewData, searchReviewData, isSearchingReviews, colorId },
      showReviews,
      params,
      location
    } = this.props;

    if (!showReviews || !detail) {
      return null;
    }

    const { brandName, productName, productId } = detail;

    const { reviews: searchResults, searchTerm } = searchReviewData;
    const { reviews: productReviews, submittedReviews, loadingReviews } = reviewData;

    const reviewsToDisplay = isSearchingReviews ? searchResults : productReviews;
    let totalPagesWithOffset = reviewsTotalPages - Math.floor(reviewsStart / PRODUCT_REVIEWS_PER_PAGE);
    if (totalPagesWithOffset < 1) {
      totalPagesWithOffset = 1;
    }

    if (!productReviews || productReviews.length === 0) {
      return (
        <MartyContext.Consumer>
          {({ testId }) => (
            <div className={css.noReviewsContainer}>
              <div className={css.noReviews} data-test-id={testId('noReviewsTextRP')}>
                This product currently has 0 reviews.
              </div>
            </div>
          )}
        </MartyContext.Consumer>
      );
    }

    return (
      <MartyContext.Consumer>
        {({ testId }) => (
          <div className={css.reviewListContainer}>
            <h2>Additional Reviews</h2>
            <ReviewSearch initialValue={searchTerm} searchReviews={this.onSearchReviews} />
            {reviewsToDisplay?.length ? (
              <div>
                <div className={css.reviewSortPaginate}>
                  <div className={css.reviewSort}>
                    <ReviewSort orderBy={orderBy} onSortReviewsClick={this.handleSortReviewsClick} />
                  </div>
                </div>
                <div className={css.reviewList}>
                  {reviewsToDisplay.map(review => (
                    <div key={review.id} className={css.reviewWrapper}>
                      <Review
                        brandName={brandName}
                        review={review}
                        productName={productName}
                        loadingReviews={loadingReviews}
                        submittedReviews={submittedReviews}
                        onReviewUpvoteClick={this.handleReviewUpvoteClick}
                        onReviewMediaClick={this.handleReviewMediaClick}
                      />
                    </div>
                  ))}
                </div>
                <ReviewGalleryWrapper params={params} returnUrl={`/product/review/${productId}${colorId ? `/color/${colorId}` : ''}`} />
              </div>
            ) : (
              <div className={css.noReviews} data-test-id={testId('noSearchResults')}>
                We were unable to find anything related to your search. If you can’t find it here, check out our{' '}
                <MartyLink to="/c/general-questions">Customer Questions Section</MartyLink> or <MartyLink to="/c/contact-us">Contact Us</MartyLink>{' '}
                to get advice from one of our helpful call center representatives.
              </div>
            )}
            <div className={css.paginationWrapper} data-test-id={testId('paginationControls')}>
              <Pagination
                currentLocation={location.pathname}
                firstPageIndex={1}
                page={reviewsPage}
                desktopStyled={true}
                totalPages={totalPagesWithOffset}
              />
            </div>
          </div>
        )}
      </MartyContext.Consumer>
    );
  }

  makeReviewUrl(productId: string, page: string | number, start?: string | number, orderBy?: string) {
    return `/product/review/${productId}/page/${page}${start ? `/start/${start}` : ''}${orderBy ? `/orderBy/${orderBy}` : ''}`;
  }

  render() {
    const {
      product: { colorId, reviewData },
      product
    } = this.props;
    if (!product.detail || product.isLoading || reviewData.isLoading) {
      return <PageLoader />;
    }

    const { reviews: productReviews } = reviewData;

    const { brandName, productId, productName, defaultImageUrl } = product.detail;
    const paginationData = this.getPaginationData();
    const breadcrumbs = [
      {
        to: buildSeoProductUrl(product.detail, colorId),
        text: `${brandName} ${productName}`
      }
    ];

    return (
      <MartyContext.Consumer>
        {context => {
          this.context = context;
          const { testId } = context;
          return (
            <ProductContextProvider value={product}>
              <SiteAwareMetadata>
                <div className={css.wrap} itemScope itemType="http://schema.org/Product">
                  <meta itemProp="name" content={productName} />
                  <div className={css.productReviews}>
                    <Breadcrumbs links={breadcrumbs} currentText="Review Summary" />
                    {this.makePageHeading()}
                    {(!productReviews || productReviews.length === 0) && (
                      <>
                        <div className={css.lottieWrapper}>
                          <EmptyReviewLlama loop={true} />
                        </div>
                        <h2 className={css.lottieHeader}>Can't Seem to Find Any...</h2>
                        <p className={css.lottiePara}>Be the first to leave a review</p>
                      </>
                    )}
                    <MartyLink
                      onClick={this.handleWriteReviewClick}
                      className={css.writeReviewButton}
                      data-test-id={testId('writeAReviewButton')}
                      to={`/product/review/add/${productId}`}
                    >
                      Write a Review
                    </MartyLink>
                    {productReviews && productReviews?.length > 0 && (
                      <>
                        <ReviewSummary product={product} />
                        {this.makeReviewContent(paginationData)}
                      </>
                    )}
                  </div>
                  {productReviews && productReviews?.length > 0 && (
                    <>
                      <meta itemProp="image" content={defaultImageUrl} />
                      <div itemProp="brand" itemType="https://schema.org/Brand" itemScope>
                        <meta itemProp="name" content={brandName} />
                      </div>
                      <meta itemProp="sku" content={productId} />
                    </>
                  )}
                </div>
              </SiteAwareMetadata>
            </ProductContextProvider>
          );
        }}
      </MartyContext.Consumer>
    );
  }
}

export function mapStateToProps(state: AppState, ownProps: any) {
  const {
    reviews: { reviewGallery },
    product
  } = state;

  const {
    match: { params }
  } = ownProps;

  return {
    product,
    reviewGallery,
    showReviews,
    params
  };
}

const mapDispatchToProps = {
  loadProductReviewsPage,
  fetchProductReviews,
  fetchReviewSearchResults,
  exitSearchReviews,
  upvoteReview,
  setProductReviewsDocMeta,
  showReviewGalleryModal,
  hideReviewGalleryModal
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(ProductReviews);
